import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, ShieldCheck, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type EventType =
  | "setting_change"
  | "permission_change"
  | "document_access"
  | "login"
  | "export"
  | "impossible_travel"
  | "honeytoken"
  | "security_alert";

interface SecurityEvent {
  id: string;
  at: string;
  type: EventType;
  subtype: string | null;
  actor: { id: string | null; name: string | null; email: string | null };
  target: string | null;
  ip: string | null;
  details: Record<string, unknown>;
}

interface PageEnvelope {
  rows: SecurityEvent[];
  total: number;
  page: number;
  pageSize: number;
}

const TYPE_OPTIONS: Array<{ key: EventType; label: string }> = [
  { key: "setting_change", label: "Setting changes" },
  { key: "permission_change", label: "Permission changes" },
  { key: "document_access", label: "Document access" },
  { key: "login", label: "Logins" },
  { key: "export", label: "Bulk exports" },
  { key: "impossible_travel", label: "Impossible travel" },
  { key: "honeytoken", label: "Honeytoken touches" },
  { key: "security_alert", label: "Other security alerts" },
];

const TYPE_LABEL: Record<EventType, string> = Object.fromEntries(
  TYPE_OPTIONS.map((o) => [o.key, o.label]),
) as Record<EventType, string>;

const PAGE_SIZE = 50;

export function SecurityEventsPanel() {
  const { toast } = useToast();
  const [selectedTypes, setSelectedTypes] = useState<Set<EventType>>(new Set());
  const [actorUserId, setActorUserId] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (selectedTypes.size > 0) p.set("types", Array.from(selectedTypes).join(","));
    if (actorUserId.trim()) p.set("actorUserId", actorUserId.trim());
    if (q.trim()) p.set("q", q.trim());
    if (from) p.set("from", new Date(from).toISOString());
    if (to) p.set("to", new Date(to).toISOString());
    p.set("page", String(page));
    p.set("pageSize", String(PAGE_SIZE));
    return p.toString();
  }, [selectedTypes, actorUserId, q, from, to, page]);

  const { data, isLoading, isFetching } = useQuery<PageEnvelope>({
    queryKey: ["/api/admin/security-events", queryString],
    queryFn: async () => {
      const res = await fetch(`/api/admin/security-events?${queryString}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const toggleType = (k: EventType) => {
    const next = new Set(selectedTypes);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSelectedTypes(next);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedTypes(new Set());
    setActorUserId("");
    setQ("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`/api/admin/security-events/export.csv?${queryString}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `security-events-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Unknown error";
      toast({ title: "Export failed", description: message, variant: "destructive" });
    }
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" />
          Security Logs
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          A unified, read-only audit log of security-relevant activity in your workspace —
          setting changes, permission changes, document access, logins, bulk exports, and
          anomaly alerts.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Narrow the list, then export the result to CSV.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {TYPE_OPTIONS.map((opt) => {
              const active = selectedTypes.has(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => toggleType(opt.key)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover-elevate"
                  }`}
                  data-testid={`filter-type-${opt.key}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Search</label>
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="email, target, action…"
                data-testid="input-security-events-search"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Actor user ID</label>
              <Input
                value={actorUserId}
                onChange={(e) => { setActorUserId(e.target.value); setPage(1); }}
                placeholder="user UUID"
                data-testid="input-security-events-actor"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">From</label>
              <Input
                type="datetime-local"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1); }}
                data-testid="input-security-events-from"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To</label>
              <Input
                type="datetime-local"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1); }}
                data-testid="input-security-events-to"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              data-testid="button-security-events-reset"
            >
              <X className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExport}
              data-testid="button-security-events-export"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Events</span>
            <span className="text-xs font-normal text-muted-foreground" data-testid="text-security-events-count">
              {isLoading ? "loading…" : `${total} matching`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data || data.rows.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground" data-testid="text-security-events-empty">
              No security events match these filters yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r) => (
                    <TableRow key={r.id} data-testid={`row-security-event-${r.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(r.at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.actor.name || r.actor.email || r.actor.id || (
                          <span className="text-muted-foreground">system</span>
                        )}
                        {r.actor.email && r.actor.name && (
                          <div className="text-muted-foreground">{r.actor.email}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="secondary">{TYPE_LABEL[r.type] ?? r.type}</Badge>
                        {r.subtype && (
                          <div className="text-muted-foreground mt-0.5 font-mono">{r.subtype}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[260px] break-words">
                        {r.target || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {r.ip || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs max-w-[320px]">
                        <code className="block whitespace-pre-wrap text-[11px] text-muted-foreground">
                          {JSON.stringify(r.details ?? {}, null, 0).slice(0, 200)}
                        </code>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
                <span>
                  Page {data.page} of {totalPages} ({total} events)
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || isFetching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="button-security-events-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || isFetching}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="button-security-events-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
